import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useParams, useNavigate } from "react-router-dom";
import { getFirstTouch } from "../utils/referrerCapture";
import { collectHardwareFingerprint } from "../utils/hardwareFingerprint";
import { 
  CheckCircle, 
  AlertTriangle, 
  Building2, 
  User, 
  Mail, 
  MessageSquare,
  MapPin,
  Globe,
  Clock,
  MousePointer,
  Headphones,
  Phone,
  BarChart3,
  Target,
  DollarSign,
  FileCheck,
  CreditCard,
  Send,
  Loader2,
  Link as LinkIcon,
  ArrowLeft,
  Shield,
  ShieldAlert,
  ShieldX,
  Info,
  XCircle,
  AlertCircle,
  Upload,
  IdCard,
  Zap,
  Search,
  ShoppingBag,
  BookOpen,
  Sparkles,
  Camera,
  X as XIcon,
  FileText,
} from "lucide-react";
import axios from "axios";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { PRIMARY_SOURCES, sourceByKey } from "../config/trafficSources";
import { MultiStateSelect } from "../components/MultiStateSelect";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Form Options
const COUNTRIES = [
  "United States",
  "Canada", 
  "United Kingdom",
  "India",
  "Pakistan",
  "Bangladesh",
  "Philippines",
  "Other"
];

const HOW_HEARD_OPTIONS = [
  "Facebook",
  "Instagram",
  "LinkedIn",
  "Twitter/X",
  "TikTok",
  "YouTube",
  "Google Search",
  "Referral from Friend/Colleague",
  "Affiliate Forum",
  "Blog/Article",
  "Email",
  "Other"
];

const EXPERIENCE_OPTIONS = [
  "Less than 3 months",
  "3 – 6 months",
  "6 – 12 months",
  "1 – 2 years",
  "2+ years"
];

// ─── Primary Traffic Source config lives in ../config/trafficSources ────
// The full definition is shared with OfferApplicationPage so the two flows
// never drift. Only the terms text below is form-specific.

// Dynamic terms text per traffic source path
const TERMS_BY_SOURCE = {
  google_ads:     { threshold: "$50", buffer: "120 sec post-IVR", payment: "Weekly Net 7"  },
  meta_ads:       { threshold: "$50", buffer: "120 sec post-IVR", payment: "Weekly Net 7"  },
  seo:            { threshold: "$50", buffer: "120 sec post-IVR", payment: "Weekly Net 7"  },
  google_my_business: { threshold: "$50", buffer: "120 sec post-IVR", payment: "Weekly Net 7"  },
  blogs:          { threshold: "$50", buffer: "120 sec post-IVR", payment: "Weekly Net 7"  },
  fb_marketplace: { threshold: "$50", buffer: "150 sec",          payment: "Monthly Net 10" },
  others:         { threshold: "$50", buffer: "150 sec",          payment: "Monthly Net 10" },
};


const CALL_TRACKING_PLATFORMS = [
  "Ringba",
  "Retreaver",
  "CallRail",
  "Invoca",
  "Other",
  "None"
];

const CALL_TYPES = [
  "Inbound Calls Only",
  "Inbound + Transfers",
  "Call Center Generated Calls"
];

const DAILY_VOLUMES = [
  "5 – 10 Calls",
  "10 – 25 Calls",
  "25 – 50 Calls",
  "50 – 100 Calls",
  "100 – 250 Calls",
  "250+ Calls"
];

const VERTICALS = [
  "Plumbers",
  "HVAC (Air Conditioning)",
  "Heating Contractors",
  "Roofers",
  "Electricians",
  "Painters",
  "Bathroom Remodeling",
  "Appliance Repair",
  "Movers",
  "Junk Removal",
  "Water & Fire Damage Restoration",
  "Windows Installers",
  "Solar Installation",
  "Exterminators (Pest Control)",
  "Garage Door Services",
  "Gutters",
  "Locksmith",
  "Chimney Services",
  "Air Duct Cleaning",
  "Dumpster Rentals",
  "Dumpster & Toilet Rentals",
  "Porta Potty Rentals",
  "Flight Booking"
];

const PRIMARY_VERTICALS = [
  "Plumbing",
  "HVAC",
  "Roofing",
  "Solar",
  "Pest Control",
  "Dumpster Rentals",
  "Flight Booking",
  "Other"
];

const TARGET_GEOS = [
  "USA Nationwide",
  "Specific States",
  "Canada",
  "UK",
  "Other"
];

const DAILY_BUDGETS = [
  "Under $100",
  "$100 – $500",
  "$500 – $1000",
  "$1000 – $5000",
  "$5000+"
];

// Components
const SectionTitle = ({ icon: Icon, title, number }) => (
  <div className="flex items-center gap-3 mb-4">
    {number ? (
      <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
        {number}
      </div>
    ) : null}
    <Icon className="w-5 h-5 text-blue-600" />
    <h3 className="font-['Poppins'] text-lg font-semibold text-slate-800">{title}</h3>
  </div>
);

const InputField = ({ label, required, ...props }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-slate-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-slate-800"
      {...props}
    />
  </div>
);

const SelectField = ({ label, required, options, ...props }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-slate-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-slate-800 bg-white"
      {...props}
    >
      <option value="">Select an option</option>
      {options.map((opt, i) => (
        <option key={i} value={opt}>{opt}</option>
      ))}
    </select>
  </div>
);

const MultiSelect = ({ label, required, options, selected, onChange }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-slate-700 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
      {options.map((opt, i) => (
        <label
          key={i}
          className={`flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer transition-all text-sm min-h-[44px] ${
            selected.includes(opt)
              ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white border-blue-600"
              : "bg-white text-slate-700 border-slate-200 hover:border-blue-400"
          }`}
        >
          <input
            type="checkbox"
            checked={selected.includes(opt)}
            onChange={(e) => {
              if (e.target.checked) {
                onChange([...selected, opt]);
              } else {
                onChange(selected.filter(s => s !== opt));
              }
            }}
            className="sr-only"
          />
          <CheckCircle className={`w-4 h-4 flex-shrink-0 ${selected.includes(opt) ? "opacity-100" : "opacity-0"}`} />
          <span className="leading-tight">{opt}</span>
        </label>
      ))}
    </div>
  </div>
);

const RadioGroup = ({ label, required, options, value, onChange, name }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-slate-700 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="flex gap-4">
      {options.map((opt, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl border cursor-pointer transition-all ${
            String(value) === opt.value
              ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white border-blue-600"
              : "bg-white text-slate-700 border-slate-200 hover:border-blue-400"
          }`}
        >
          <span>{opt.label}</span>
        </button>
      ))}
    </div>
  </div>
);

// Main Component
const PublisherApplicationPage = () => {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    // Microsoft Teams ID — collected explicitly (renamed from Skype/Telegram).
    contact_method: "",
    messaging_platform: "",
    messaging_handle: "",
    company_name: "",
    publisher_location: "",
    country: "",
    experience_duration: "",
    previous_companies: "",
    how_heard_about_us: "",
    traffic_sources: [],
    call_tracking_platform: "",
    call_type: "",
    daily_call_volume: "",
    payment_method: "",
    payment_details: "",
    verticals: [],
    offer_approval_links: "",
    agreed_to_terms: false,
    digital_signature: "",
    // Handwritten canvas signature — dataURL (PNG). Cleared on Clear.
    signature_image: "",
  });

  const [fingerprintId, setFingerprintId] = useState("");
  // Anti-bot: mark when the form was first mounted so we can measure how
  // fast the visitor completed it. Any submission under 20 s is flagged as
  // Velocity-Bot on the backend.
  const [formOpenAt] = useState(() => Date.now());
  // Deep hardware signals — captured separately so duplicates get flagged
  // even when the visitor is behind a VPN or has cleared cookies.
  const [hwFingerprint, setHwFingerprint] = useState({
    fp_canvas_hash: "",
    fp_webgl_hash: "",
    fp_hardware_hash: "",
    fp_audio_hash: "",
    fp_fonts_hash: "",
    fp_media_hash: "",
    hardware_id: "",
    super_hardware_id: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  // Live duplicate-email lookup. Debounced 500ms whenever the email changes.
  // status: idle | checking | available | taken
  const [emailAvailability, setEmailAvailability] = useState({ status: "idle", message: "" });

  // Traffic source + source-specific fields + proof-of-work.
  // The source key is now driven by the URL param (/apply/:source) so each
  // vertical has its own dedicated form page. If the user lands on /apply
  // (no param), we show ONLY the picker cards which navigate on click.
  const { source: sourceParam } = useParams();
  const navigate = useNavigate();
  const initialSource = sourceParam && sourceByKey(sourceParam) ? sourceParam : "";
  const [primaryTrafficSource, setPrimaryTrafficSource] = useState(initialSource);
  const [sourceDetails, setSourceDetails] = useState({});
  const [proofFiles, setProofFiles] = useState([]);       // File[]
  const [proofPreviews, setProofPreviews] = useState([]); // dataURL[]

  // Structured per-offer proof uploads (Google Ads / Meta Ads only):
  // { [vertical]: { [slotKey]: File } } with matching previews.
  const [slotProofFiles, setSlotProofFiles] = useState({});
  const [slotProofPreviews, setSlotProofPreviews] = useState({});

  // Path-specific "I agree to these terms" checkbox (mandatory)
  const [agreedToTrafficTerms, setAgreedToTrafficTerms] = useState(false);

  // Keep state in sync when the URL param changes (e.g. user opens /apply/seo)
  useEffect(() => {
    if (sourceParam && sourceByKey(sourceParam)) {
      setPrimaryTrafficSource(sourceParam);
    } else if (!sourceParam) {
      setPrimaryTrafficSource("");
    }
  }, [sourceParam]);

  // Live duplicate-email check. Debounced 500 ms whenever the email field
  // changes so the backend isn't hit on every keystroke. Uses AbortController
  // to cancel outdated requests when the user keeps typing.
  useEffect(() => {
    const raw = (formData.email || "").trim();
    if (!raw || !/\S+@\S+\.\S+/.test(raw)) {
      setEmailAvailability({ status: "idle", message: "" });
      return;
    }
    setEmailAvailability({ status: "checking", message: "" });
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/publisher/email-check?email=${encodeURIComponent(raw)}`,
          { signal: ctrl.signal }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setEmailAvailability(
          data.exists
            ? { status: "taken", message: data.message || "" }
            : { status: "available", message: "" }
        );
      } catch (e) {
        if (e.name !== "AbortError") {
          // Network hiccup — don't block the applicant; final backend guard
          // still enforces uniqueness at submit time.
          setEmailAvailability({ status: "idle", message: "" });
        }
      }
    }, 500);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [formData.email]);

  // Proof file handlers ------------------------------------------------
  const handleProofSelect = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3 - proofFiles.length);
    const ALLOWED = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif", "application/pdf"];
    const MAX = 10 * 1024 * 1024; // 10 MB
    const ok = files.filter((f) => ALLOWED.includes(f.type) && f.size <= MAX);
    if (ok.length !== files.length) {
      alert("Only JPG / PNG / PDF up to 10 MB allowed.");
    }
    const nextFiles = [...proofFiles, ...ok].slice(0, 3);
    setProofFiles(nextFiles);
    // Build previews
    Promise.all(
      nextFiles.map(
        (f) =>
          new Promise((res) => {
            if (f.type === "application/pdf") return res({ pdf: true, name: f.name });
            const r = new FileReader();
            r.onload = () => res({ url: r.result, name: f.name });
            r.readAsDataURL(f);
          }),
      ),
    ).then(setProofPreviews);
    if (e.target) e.target.value = "";
  };
  const removeProof = (i) => {
    setProofFiles((prev) => prev.filter((_, idx) => idx !== i));
    setProofPreviews((prev) => prev.filter((_, idx) => idx !== i));
  };

  // Per-offer slot proof handlers (Google Ads / Meta Ads) ----------------
  const handleSlotProofSelect = (vertical, slotKey) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ALLOWED = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif", "application/pdf"];
    const MAX = 10 * 1024 * 1024; // 10 MB
    if (!ALLOWED.includes(file.type) || file.size > MAX) {
      alert("Only JPG / PNG / PDF up to 10 MB allowed.");
      if (e.target) e.target.value = "";
      return;
    }
    setSlotProofFiles((prev) => ({ ...prev, [vertical]: { ...(prev[vertical] || {}), [slotKey]: file } }));
    if (file.type === "application/pdf") {
      setSlotProofPreviews((prev) => ({ ...prev, [vertical]: { ...(prev[vertical] || {}), [slotKey]: { pdf: true, name: file.name } } }));
    } else {
      const r = new FileReader();
      r.onload = () =>
        setSlotProofPreviews((prev) => ({ ...prev, [vertical]: { ...(prev[vertical] || {}), [slotKey]: { url: r.result, name: file.name } } }));
      r.readAsDataURL(file);
    }
    if (e.target) e.target.value = "";
  };
  const removeSlotProof = (vertical, slotKey) => {
    setSlotProofFiles((prev) => {
      const next = { ...(prev[vertical] || {}) };
      delete next[slotKey];
      return { ...prev, [vertical]: next };
    });
    setSlotProofPreviews((prev) => {
      const next = { ...(prev[vertical] || {}) };
      delete next[slotKey];
      return { ...prev, [vertical]: next };
    });
  };

  // Mirror the primary source into the legacy multi-select whenever the
  // user picks a card, so the backend's `traffic_sources` array is always
  // populated correctly.
  useEffect(() => {
    const src = sourceByKey(primaryTrafficSource);
    if (!src) return;
    setFormData((prev) => (
      prev.traffic_sources.includes(src.matchesLegacy)
        ? prev
        : { ...prev, traffic_sources: [...prev.traffic_sources, src.matchesLegacy] }
    ));
  }, [primaryTrafficSource]);

  // Individual payment-terms acknowledgements — publisher must tick each one
  // so they can't claim later they didn't see the threshold / buffer / cycle.
  const PAYMENT_ACK_RULES = [
    {
      key: "threshold",
      title: "Minimum Payout Threshold: $50",
      desc: "I understand my earnings are only released once my approved balance reaches US $50. Any balance below $50 carries over to the next cycle until the threshold is met.",
    },
    {
      key: "buffer",
      title: "Buffer Time: 150 seconds post-IVR",
      desc: "I understand only the call duration AFTER the post-IVR buffer (150 seconds) counts towards billable time. The buffer is non-payable.",
    },
    {
      key: "net10",
      title: "Payment Cycle: Monthly · Net 10",
      desc: "I understand approved earnings for a given calendar month are released on or before the 10th business day of the following month — not daily, not weekly.",
    },
  ];
  const [paymentAcks, setPaymentAcks] = useState(
    PAYMENT_ACK_RULES.reduce((acc, r) => ({ ...acc, [r.key]: false }), {})
  );
  const paymentAckedCount = Object.values(paymentAcks).filter(Boolean).length;
  const allPaymentAcks = paymentAckedCount === PAYMENT_ACK_RULES.length;
  const togglePaymentAck = (key) =>
    setPaymentAcks((prev) => ({ ...prev, [key]: !prev[key] }));

  // Individual compliance acknowledgements — publisher must tick all
  const COMPLIANCE_RULES = [
    { key: "fake_calls", title: "Fake Calls", desc: "Immediate termination with no payout", severity: "red" },
    { key: "body_massage", title: "Body Massage / Spa Calls", desc: "Immediate termination with no payout", severity: "red" },
    { key: "non_english", title: "Non-English Calls", desc: "Only English language calls will be billed", severity: "red" },
    { key: "indian_pakistani", title: "Coach calls", desc: "Calls with external agent assistance or scripted guidance detected (Non-USA callers).", severity: "amber" },
    { key: "item_selling", title: "Item / Parts Selling Calls", desc: "Not allowed (First warning issued)", severity: "amber" },
    { key: "girls_images", title: "Using Girls' Images in Posts", desc: "Not allowed (First warning issued)", severity: "amber" },
  ];
  const [complianceAcks, setComplianceAcks] = useState(
    COMPLIANCE_RULES.reduce((acc, r) => ({ ...acc, [r.key]: false }), {})
  );
  const ackedCount = Object.values(complianceAcks).filter(Boolean).length;
  const allCompliance = ackedCount === COMPLIANCE_RULES.length;
  const toggleAck = (key) =>
    setComplianceAcks((prev) => ({ ...prev, [key]: !prev[key] }));

  // Initialize FingerprintJS on component mount
  useEffect(() => {
    const initFingerprint = async () => {
      try {
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        setFingerprintId(result.visitorId);
        console.log("Device fingerprint captured:", result.visitorId);
      } catch (error) {
        console.error("Fingerprint error:", error);
      }
      // Independently collect canvas / webgl / hardware hashes — even if the
      // FingerprintJS load itself fails we still get VPN-proof signals.
      try {
        const hw = await collectHardwareFingerprint();
        setHwFingerprint(hw);
        console.log("Hardware fingerprint captured:", hw);
      } catch (e) {
        console.error("Hardware fingerprint error:", e);
      }
    };
    initFingerprint();
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ============================================================
    // PRE-FLIGHT VALIDATION — catch missing fields BEFORE we hit
    // the network so the user gets a friendly checklist instead of
    // a useless "Submission failed (HTTP 422)" from the server.
    // Each entry maps to the exact Pydantic-required field on the
    // backend so the two stay in lock-step.
    // ============================================================
    const REQUIRED = [
      { key: "full_name",              label: "Full Name",                          test: (v) => !!(v || "").trim() },
      { key: "email",                  label: "Email Address",                      test: (v) => /\S+@\S+\.\S+/.test(v || "") },
      { key: "phone",                  label: "Phone Number",                       test: (v) => (v || "").replace(/\D/g, "").length >= 7 },
      { key: "contact_method",         label: "Microsoft Teams ID",                 test: (v) => !!(v || "").trim() },
      { key: "country",                label: "Country",                            test: (v) => !!(v || "").trim() },
      { key: "publisher_location",     label: "City / State",                       test: (v) => !!(v || "").trim() },
      { key: "messaging_platform",     label: "Preferred Messaging Platform",       test: (v) => !!(v || "").trim() },
      { key: "messaging_handle",       label: "Messaging Handle",                   test: (v) => !!(v || "").toString().trim() },
      { key: "experience_duration",    label: "Years of Experience",                test: (v) => !!(v || "").trim() },
      { key: "traffic_sources",        label: "Traffic Sources (at least one)",     test: (v) => Array.isArray(v) && v.length > 0 },
      { key: "call_tracking_platform", label: "Call Tracking Platform",             test: (v) => !!(v || "").trim() },
      { key: "call_type",              label: "Call Type",                          test: (v) => !!(v || "").trim() },
      { key: "daily_call_volume",      label: "Daily Call Volume",                  test: (v) => !!(v || "").trim() },
      { key: "payment_method",         label: "How do we pay you? (Payment Method)", test: (v) => !!(v || "").trim() },
      { key: "payment_details",        label: "Payment Details (email / account)",  test: (v) => !!(v || "").trim() },
      { key: "verticals",              label: "Verticals (at least one)",           test: (v) => Array.isArray(v) && v.length > 0 },
      { key: "agreed_to_terms",        label: "Agreement to Terms",                 test: (v) => v === true },
      { key: "digital_signature",      label: "Digital Signature",                  test: (v) => !!(v || "").trim() },
      { key: "signature_image",        label: "Handwritten Signature (drawn)",      test: (v) => !!(v || "").trim() },
    ];

    const missing = REQUIRED.filter((r) => !r.test(formData[r.key]));
    const missingLabels = missing.map((r) => r.label);

    // Traffic Source & Proof — dynamic mandatory validation
    const chosenSrc = sourceByKey(primaryTrafficSource);
    if (!chosenSrc) {
      missingLabels.push("Primary Traffic Source");
    } else {
      chosenSrc.fields.filter((f) => f.required).forEach((f) => {
        if (f.type === "file") {
          // File-type fields are covered by the mandatory Proof-of-Work check.
          return;
        }
        if (f.multiSelect) {
          const arr = Array.isArray(sourceDetails[f.name]) ? sourceDetails[f.name] : [];
          if (arr.length === 0) missingLabels.push(`${chosenSrc.label} · ${f.label}`);
          return;
        }
        if (f.listField) {
          const arr = Array.isArray(sourceDetails[f.name]) ? sourceDetails[f.name] : [];
          const ok = arr.some((v) => (v || "").toString().trim());
          if (!ok) missingLabels.push(`${chosenSrc.label} · ${f.label} (at least one)`);
        } else {
          const v = (sourceDetails[f.name] ?? "").toString().trim();
          if (!v) missingLabels.push(`${chosenSrc.label} · ${f.label}`);
        }
      });
      if (chosenSrc.proofSlots) {
        // Structured per-offer proof: every selected vertical needs all 3 slots.
        const verts = Array.isArray(formData.verticals) ? formData.verticals : [];
        if (verts.length === 0) {
          missingLabels.push(`${chosenSrc.label} · Proof of Work (select your offers / verticals in Section 7 first)`);
        } else {
          verts.forEach((v) => {
            chosenSrc.proofSlots.forEach((slot) => {
              if (!slotProofFiles?.[v]?.[slot.key]) {
                missingLabels.push(`${chosenSrc.label} Proof — ${v} · ${slot.label}`);
              }
            });
          });
        }
      } else if (proofFiles.length === 0) {
        missingLabels.push(`${chosenSrc.label} · Proof of Work (at least 1 screenshot / PDF)`);
      }
      // Only enforce the traffic-terms agreement when the chosen source
      // actually has a rendered <DynamicTrafficTerms> block (i.e. it exists
      // in TERMS_BY_SOURCE). Sources without a terms block (e.g. GMB) would
      // otherwise be impossible to submit because there is no checkbox to tick.
      const hasTermsBlock = !!TERMS_BY_SOURCE[primaryTrafficSource];
      if (hasTermsBlock && !agreedToTrafficTerms) {
        missingLabels.push(`${chosenSrc.label} · Agreement to traffic-specific terms`);
      }
    }

    if (!allCompliance)  missingLabels.push(`All ${COMPLIANCE_RULES.length} Compliance Acknowledgements`);

    if (missingLabels.length > 0) {
      setSubmitStatus({
        type: "error",
        message:
          missingLabels.length === 1
            ? `Please complete this required field: ${missingLabels[0]}.`
            : `Please complete the following ${missingLabels.length} required items before submitting:\n• ${missingLabels.join("\n• ")}`,
      });

      // Scroll to the first missing field so the user sees where to go.
      const firstKey = missing[0]?.key;
      let targetSelector;
      if (firstKey) {
        targetSelector = `[name="${firstKey}"], [data-testid="${firstKey}"], [data-testid="${firstKey}-input"]`;
      } else if (!primaryTrafficSource || missingLabels.some((l) => l.startsWith("Primary Traffic Source") || (chosenSrc && l.startsWith(chosenSrc.label)))) {
        targetSelector = '[data-testid="traffic-source-section"]';
      } else if (!allCompliance) {
        targetSelector = '[data-testid="compliance-section"]';
      } else {
        targetSelector = '[data-testid="compliance-section"]';
      }
      const el = document.querySelector(targetSelector);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        // Briefly focus the field so screen-readers and keyboard users land on it
        if (typeof el.focus === "function") {
          setTimeout(() => el.focus({ preventScroll: true }), 350);
        }
      } else {
        // Fall back to top of the form
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Create FormData for multipart submission
      const submitData = new FormData();

      // Derive a sensible `contact_method` value for the backend if blank —
      // schema requires it; we prefer the chosen messaging platform, fall back to email.
      const contactMethod = (formData.contact_method || "").trim()
        || (formData.messaging_platform || "").trim()
        || "email";

      // Add all form fields
      submitData.append("full_name", (formData.full_name || "").trim());
      submitData.append("email", (formData.email || "").trim());
      submitData.append("phone", (formData.phone || "").trim());
      submitData.append("contact_method", contactMethod);
      submitData.append("messaging_platform", formData.messaging_platform || "");
      submitData.append("messaging_handle", (formData.messaging_handle || "").trim());
      submitData.append("company_name", formData.company_name || "");
      submitData.append("publisher_location", formData.publisher_location);
      submitData.append("country", formData.country);
      submitData.append("experience_duration", formData.experience_duration);
      submitData.append("previous_companies", formData.previous_companies || "");
      submitData.append("how_heard_about_us", formData.how_heard_about_us || "");
      submitData.append("traffic_sources", JSON.stringify(formData.traffic_sources));
      submitData.append("primary_traffic_source", primaryTrafficSource || "");
      submitData.append("source_details", JSON.stringify(sourceDetails || {}));
      const chosenForProof = sourceByKey(primaryTrafficSource);
      if (chosenForProof?.proofSlots) {
        // Structured per-offer proof: verticals (as selected) × slots (config order).
        const manifest = [];
        (Array.isArray(formData.verticals) ? formData.verticals : []).forEach((v) => {
          chosenForProof.proofSlots.forEach((slot) => {
            const f = slotProofFiles?.[v]?.[slot.key];
            if (f) {
              submitData.append("proof_files", f);
              manifest.push({ vertical: v, slot: slot.key, slot_label: slot.label, file_name: f.name });
            }
          });
        });
        submitData.append("proof_manifest", JSON.stringify(manifest));
      } else {
        proofFiles.forEach((pf) => submitData.append("proof_files", pf));
      }
      submitData.append("call_tracking_platform", formData.call_tracking_platform);
      submitData.append("payment_method", formData.payment_method || "");
      submitData.append("payment_details", formData.payment_details || "");
      submitData.append("call_type", formData.call_type);
      submitData.append("daily_call_volume", formData.daily_call_volume);
      submitData.append("verticals", JSON.stringify(formData.verticals));
      submitData.append("offer_approval_links", formData.offer_approval_links || "");
      submitData.append("agreed_to_terms", formData.agreed_to_terms);
      submitData.append("digital_signature", formData.digital_signature || "");
      // Handwritten signature image — attach as a File blob only when the
      // applicant actually drew something on the canvas (the state is a
      // dataURL PNG). Empty/omitted just means "typed signature only".
      if (formData.signature_image) {
        try {
          const parts = formData.signature_image.split(",");
          const bin = atob(parts[1] || "");
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          const blob = new Blob([bytes], { type: "image/png" });
          submitData.append("signature_image", blob, "signature.png");
        } catch (e) {
          console.warn("Could not attach signature image:", e);
        }
      }
      submitData.append("fingerprint_id", fingerprintId);
      // Deep hardware signals for VPN-proof duplicate detection
      submitData.append("fp_canvas_hash", hwFingerprint.fp_canvas_hash || "");
      submitData.append("fp_webgl_hash", hwFingerprint.fp_webgl_hash || "");
      submitData.append("fp_hardware_hash", hwFingerprint.fp_hardware_hash || "");
      submitData.append("hardware_id", hwFingerprint.hardware_id || "");
      submitData.append("super_hardware_id", hwFingerprint.super_hardware_id || "");
      submitData.append("fp_audio_hash", hwFingerprint.fp_audio_hash || "");
      submitData.append("fp_fonts_hash", hwFingerprint.fp_fonts_hash || "");
      submitData.append("fp_media_hash", hwFingerprint.fp_media_hash || "");
      // Form-fill velocity — how many seconds elapsed since page load
      submitData.append(
        "form_duration_seconds",
        String(((Date.now() - formOpenAt) / 1000).toFixed(2))
      );

      // Conversion attribution: send the ORIGINAL external referrer captured
      // on the visitor's first pageview (persisted in localStorage), plus the
      // browser's current document.referrer as a fallback / debug signal.
      const firstTouch = getFirstTouch();
      const referringUrl =
        (firstTouch && firstTouch.referrer) || document.referrer || "";
      submitData.append("referring_url", referringUrl.slice(0, 500));
      submitData.append(
        "landing_page",
        (firstTouch && firstTouch.landing) ||
          `${window.location.pathname}${window.location.search}`.slice(0, 500),
      );
      
      console.log("Submitting form with fingerprint:", fingerprintId);

      // 120-second hard timeout — ID verification + Cloudinary upload + PDF
      // generation can legitimately take 30-60s on slow connections, but
      // anything beyond 120s means something is wedged and we should bail
      // gracefully instead of leaving the user staring at a spinner.
      // Mobile uploads on weak signal are slow, so we err on the safe side.
      const TIMEOUT_MS = 120_000;
      const MAX_ATTEMPTS = 2;   // 1 initial try + 1 silent retry on transient network errors

      // Helper that performs the request with timeout. Returns a Response
      // (even on HTTP errors) or throws on network-level failures.
      const submitOnce = async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);
        try {
          return await fetch(`${API_URL}/api/publisher/apply`, {
            method: 'POST',
            body: submitData,
            signal: controller.signal,
          });
        } finally {
          clearTimeout(timeoutId);
        }
      };

      // Inline retry message while we make additional attempts. We keep this
      // soft so it doesn't flash a red error in front of a publisher whose
      // request will succeed on the second try.
      const showRetrying = (attempt) =>
        setSubmitStatus({
          type: "info",
          message:
            `Network hiccup — automatically retrying (${attempt}/${MAX_ATTEMPTS - 1})… ` +
            `please keep this tab open.`,
        });

      let response;
      let lastNetworkError = null;
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          response = await submitOnce();
          lastNetworkError = null;
          break; // fetch resolved (even with an HTTP error code) — exit retry loop
        } catch (netErr) {
          lastNetworkError = netErr;
          const raw = String(netErr?.message || "");
          const isTransient =
            /failed to fetch|networkerror|load failed/i.test(raw) ||
            netErr?.name === "TypeError";
          // Don't retry on user-aborted, extension-interference, or unknown
          // shape errors — those won't get better by retrying.
          if (attempt < MAX_ATTEMPTS && isTransient) {
            showRetrying(attempt);
            // Short backoff before retry so the network can recover.
            await new Promise((r) => setTimeout(r, 1500));
            continue;
          }
          throw netErr;
        }
      }
      if (!response) throw lastNetworkError || new Error("No response received");

      // Parse the response safely — never let a JSON parse failure swallow
      // the real HTTP error. Some edge cases (ingress timeout on slow AI
      // verification, 413 too-large, etc.) return HTML/plain text.
      let data = {};
      let rawText = "";
      try {
        rawText = await response.text();
        data = rawText ? JSON.parse(rawText) : {};
      } catch (_parseErr) {
        data = { detail: rawText?.slice(0, 500) || "" };
      }
      console.log("Response:", response.status, data);

      if (response.ok) {
        setSubmitStatus({ type: "success", message: data.message, id: data.application_id });
      } else {
        // Prefer the backend's explicit error message. Add contextual hints
        // for the most common rejection reasons so the user knows what to fix.
        let errorMessage = "";
        if (data.detail) {
          errorMessage = Array.isArray(data.detail)
            ? data.detail.map(e => `${e.loc?.join('.') || 'field'}: ${e.msg}`).join(', ')
            : data.detail;
        }

        // Friendly summaries for well-known rejection codes/messages
        const lower = (errorMessage || "").toLowerCase();
        if (response.status === 409 && lower.includes("already exists")) {
          // Duplicate email — mirror the inline hint state so the field
          // shows red even if the applicant bypassed the debounced check
          // by rapidly hitting submit.
          setEmailAvailability({ status: "taken", message: errorMessage });
          errorMessage = errorMessage; // keep the canonical backend copy
        } else if (response.status === 400 && lower.includes("government id")) {
          errorMessage = `❌ Invalid ID: ${errorMessage} Tip: use good lighting, fill the frame, no screenshots or memes.`;
        } else if (response.status === 400 && lower.includes("jpg") && lower.includes("png")) {
          errorMessage = `❌ Invalid file type: ${errorMessage}`;
        } else if (response.status === 400 && lower.includes("too small")) {
          errorMessage = `❌ ID image is too small. Please upload a clearer, full-resolution photo (min 200 KB).`;
        } else if (response.status === 400 && lower.includes("agree to the terms")) {
          errorMessage = `Please tick all the compliance checkboxes before submitting.`;
        } else if (response.status === 413) {
          errorMessage = `File too large. Please compress your ID image (under 5 MB).`;
        } else if (response.status === 504 || response.status === 502) {
          errorMessage = `The server is taking longer than expected to verify your ID. Please try again in a moment — if it keeps failing, try a different photo.`;
        } else if (response.status >= 500) {
          // Server-side failure. Show the backend's error id (if present) so
          // the user can quote it when they reach out — much more useful than
          // a bare "HTTP 500" that tells nobody anything.
          const errorId = data.error_id || data.request_id || "";
          const hint = errorId
            ? `Reference ID: ${errorId}. Please quote this when you email abidali@digitalmastermedia.com.`
            : `Please try again in a moment — if it keeps failing, email abidali@digitalmastermedia.com with a screenshot.`;
          errorMessage = `Our server hit an unexpected error while processing your application. ${hint}`;
        } else if (!errorMessage) {
          // Bare 400 with no detail — usually a proxy/CDN sanitizing the response.
          // Most common real cause: ID consent not ticked, or photo format/size issue.
          if (response.status === 400) {
            errorMessage = (
              "Your submission was rejected. The most common causes are:\n\n" +
              "1. The 'I consent to ID verification' box wasn't ticked — scroll up to step 11 and check it.\n" +
              "2. The ID photo wasn't a clear image — try retaking with good lighting.\n" +
              "3. A browser extension is blocking the upload — try an Incognito window.\n\n" +
              "If none of these help, email abidali@digitalmastermedia.com with a screenshot."
            );
          } else {
            errorMessage = `Submission failed (HTTP ${response.status}). Please try again or email abidali@digitalmastermedia.com.`;
          }
        }

        console.error("Form submission error:", errorMessage);
        setSubmitStatus({ type: "error", message: errorMessage });
      }
    } catch (error) {
      // This only runs when fetch() itself fails (offline, DNS, CORS block,
      // request aborted, or — most commonly — a browser extension hooking
      // into fetch and breaking the Request object via window.postMessage).
      console.error("Network-level error:", error);
      const raw = String(error?.message || "");

      // 90-second timeout fired (AbortController.abort())
      const isAborted = error?.name === "AbortError" || /aborted|signal is aborted/i.test(raw);

      // Browser-extension interference signature. The extension intercepts
      // our fetch() and tries to forward the Request via window.postMessage,
      // which the browser refuses because Request objects aren't cloneable.
      // We give the publisher a concrete, actionable fix.
      const isExtensionInterference =
        /postMessage/i.test(raw) ||
        /could not be cloned/i.test(raw) ||
        /request object could not be cloned/i.test(raw);

      let msg;
      if (isAborted) {
        msg = (
          "Your submission took longer than 2 minutes and was cancelled. " +
          "This almost always means a slow internet connection or a very large ID image.\n\n" +
          "To fix:\n" +
          "1. Switch to a faster network (Wi-Fi > 4G) and keep the tab open.\n" +
          "2. Shrink your ID photo — under 2 MB works best.\n" +
          "3. Tap the button below to retry — nothing was lost."
        );
      } else if (isExtensionInterference) {
        msg = (
          "A browser extension is blocking the submission (password manager, " +
          "coupon plugin, antivirus shield, or VPN extension). To submit successfully, " +
          "please try one of the following: " +
          "(1) open this page in an Incognito / Private window, " +
          "(2) temporarily disable extensions (LastPass / 1Password / Honey / Avast / " +
          "Norton / Grammarly are the usual culprits), or " +
          "(3) try a different browser (Firefox or Safari). " +
          "Your data is safe — nothing has been submitted yet."
        );
      } else if (/failed to fetch|networkerror|load failed/i.test(raw)) {
        msg = (
          "We couldn't reach our server after 2 attempts. This usually means your " +
          "internet dropped while uploading your ID photo. To fix:\n\n" +
          "1. Make sure you have a stable Wi-Fi or 4G/5G signal (avoid switching networks mid-upload).\n" +
          "2. If your ID photo is huge (over 4 MB), retake it or compress it.\n" +
          "3. Tap the button below to try again — your form data is still here, you don't need to refill anything."
        );
      } else {
        msg = `Connection error: ${raw || "unknown"}. Please try again — your form data is preserved.`;
      }
      setSubmitStatus({ type: "error", message: msg });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus?.type === "success") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-blue-600" />
          </div>
          <h2 className="font-['Poppins'] text-2xl font-bold text-slate-800 mb-4">Application Submitted!</h2>
          <p className="text-slate-600 mb-6">
            Thank you for applying to Digital Master Media LLC.<br /><br />
            Our team will review your application and contact you if approved.
          </p>
          <p className="text-sm text-slate-500 mb-6">
            Application ID: <span className="font-mono">{submitStatus.id}</span>
          </p>
          <Link 
            to="/"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold px-6 py-3 rounded-full hover:shadow-lg transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Homepage
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 font-['Inter']">
      {/* Header */}
      <Header />

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0f172a] text-white py-8 sm:py-12 px-4 relative overflow-hidden pt-24 sm:pt-28">
        <div className="absolute top-0 left-0 w-72 h-72 bg-blue-600/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl"></div>
        <div className="max-w-4xl mx-auto text-center relative">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-xs sm:text-sm mb-3 sm:mb-4 border border-white/10">
            <User className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            Publisher Application
          </div>
          <h1 className="font-['Poppins'] text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-3">
            Become a <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Publisher</span>
          </h1>
          <p className="text-white/80 text-xs sm:text-sm md:text-base max-w-xl mx-auto">
            Join our Pay-Per-Call network and start sending high-intent inbound calls for home services and travel campaigns.
          </p>
        </div>
      </div>

      {/* Form — /apply shows only picker; /apply/:source shows the full form */}
      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto px-3 sm:px-6 py-4 sm:py-6 -mt-6" data-testid="publisher-form">
        {!primaryTrafficSource && (
          <TrafficSourcePicker
            primaryTrafficSource={primaryTrafficSource}
            onPick={(key) => navigate(`/apply/${key}`)}
          />
        )}

        {!primaryTrafficSource && (
          <div className="bg-blue-50 border border-blue-200 text-blue-900 rounded-2xl p-5 text-center text-sm" data-testid="picker-gate-message">
            👆 <span className="font-semibold">Choose a traffic source above</span> to open the application form.
          </div>
        )}

        {primaryTrafficSource && (
        <>
        {/* Header pill: chosen source + link back to picker */}
        <div
          className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3"
          data-testid="chosen-source-pill"
        >
          <button
            type="button"
            onClick={() => navigate("/apply")}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-3 sm:py-2 rounded-xl border border-slate-200 bg-white text-slate-700 hover:text-blue-700 hover:border-blue-300 hover:bg-blue-50 text-sm font-semibold shadow-sm min-h-[44px] transition-all"
            data-testid="change-source-btn"
          >
            <span aria-hidden="true">←</span>
            Choose another traffic source
          </button>
          <span className="w-full sm:w-auto text-center sm:text-right text-[11px] uppercase tracking-widest text-slate-400 font-semibold break-words">
            Applying via: <span className="text-blue-600 font-bold normal-case tracking-normal">{sourceByKey(primaryTrafficSource)?.label}</span>
          </span>
        </div>
        {/* Section 1: Basic Information */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-6 mb-6" data-testid="basic-info-section">
          <SectionTitle icon={User} title="Basic Information" number="1" />
          
          <div className="grid sm:grid-cols-2 gap-4">
            <InputField
              label="Full Name"
              required
              type="text"
              value={formData.full_name}
              onChange={(e) => handleChange("full_name", e.target.value)}
              placeholder="John Doe"
            />
            {/* Email with live duplicate-check. Disables submit + shows a
                friendly message the moment the applicant finishes typing. */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="john@example.com"
                  className={`w-full px-4 py-3 pr-10 border rounded-xl outline-none transition-all text-slate-800 focus:ring-2 ${
                    emailAvailability.status === "taken"
                      ? "border-red-400 focus:ring-red-500 focus:border-red-500 bg-red-50/50"
                      : emailAvailability.status === "available"
                      ? "border-emerald-400 focus:ring-emerald-500 focus:border-emerald-500"
                      : "border-slate-200 focus:ring-blue-500 focus:border-blue-500"
                  }`}
                  data-testid="email-input"
                />
                {emailAvailability.status === "checking" && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" data-testid="email-checking">
                    Checking…
                  </span>
                )}
                {emailAvailability.status === "available" && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-500" aria-hidden="true">✓</span>
                )}
                {emailAvailability.status === "taken" && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" aria-hidden="true">✕</span>
                )}
              </div>
              {emailAvailability.status === "taken" && (
                <p
                  className="mt-2 text-sm text-red-600 font-medium leading-snug"
                  data-testid="email-duplicate-msg"
                >
                  {emailAvailability.message ||
                    "An application with this email already exists. Please contact your Publisher Manager for further assistance."}
                </p>
              )}
            </div>
          </div>

          <InputField
            label="Phone Number"
            required
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            placeholder="+1 (555) 000-1234"
            data-testid="phone-input"
          />
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Microsoft Teams ID <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-3 flex items-center pointer-events-none" aria-hidden="true">
                <svg viewBox="0 0 32 32" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="6" width="18" height="20" rx="2" fill="#4B53BC" />
                  <text x="10" y="21" fontFamily="Arial, Helvetica, sans-serif" fontWeight="bold" fontSize="14" fill="#ffffff" textAnchor="middle">T</text>
                  <circle cx="24" cy="12" r="6" fill="#7B83EB" />
                  <text x="24" y="16" fontFamily="Arial, Helvetica, sans-serif" fontWeight="bold" fontSize="9" fill="#ffffff" textAnchor="middle">t</text>
                </svg>
              </span>
              <input
                type="text"
                required
                value={formData.contact_method}
                onChange={(e) => handleChange("contact_method", e.target.value)}
                placeholder="your.name@company.com or +12345 (Teams sign-in)"
                className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-slate-800"
                data-testid="teams-id-input"
              />
            </div>
          </div>

          {/* Microsoft Teams / Telegram — secondary messaging channel */}
          <div className="mb-4 grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Messaging Platform <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {["Microsoft Teams", "Telegram"].map((opt) => {
                  const active = formData.messaging_platform === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => {
                        handleChange("messaging_platform", opt);
                        // Reset handle if user switches platform so the
                        // placeholder hint is always relevant.
                        if (formData.messaging_platform !== opt) {
                          handleChange("messaging_handle", "");
                        }
                      }}
                      data-testid={`messaging-platform-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`px-3 py-3 rounded-xl border text-sm font-medium transition-all min-h-[56px] flex items-center justify-center text-center leading-tight ${
                        active
                          ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white border-blue-600 shadow-sm"
                          : "bg-white text-slate-700 border-slate-200 hover:border-blue-400"
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Your Exact Name on{" "}
                <span className="text-blue-700">
                  {formData.messaging_platform || "the chosen platform"}
                </span>{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.messaging_handle}
                onChange={(e) => handleChange("messaging_handle", e.target.value)}
                placeholder={
                  formData.messaging_platform === "Microsoft Teams"
                    ? "Display name as it appears on Teams (e.g., Jane Doe)"
                    : formData.messaging_platform === "Telegram"
                    ? "@username (e.g., @jane_doe)"
                    : "Select a platform first"
                }
                disabled={!formData.messaging_platform}
                data-testid="messaging-handle-input"
                autoComplete="off"
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-slate-800 disabled:bg-slate-50 disabled:text-slate-400"
              />
              <p className="text-slate-400 text-xs mt-1">
                Type the name <b>exactly</b> as it appears on{" "}
                {formData.messaging_platform || "your chosen platform"} so our team can reach you.
              </p>
            </div>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4">
            <InputField
              label="Company Name"
              type="text"
              value={formData.company_name}
              onChange={(e) => handleChange("company_name", e.target.value)}
              placeholder="Optional"
            />
            <InputField
              label="Publisher Location (City / State)"
              required
              type="text"
              value={formData.publisher_location}
              onChange={(e) => handleChange("publisher_location", e.target.value)}
              placeholder="New York, NY"
            />
          </div>
          
          <SelectField
            label="Country"
            required
            options={COUNTRIES}
            value={formData.country}
            onChange={(e) => handleChange("country", e.target.value)}
          />
        </div>

        {/* Section 2: Experience */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 mb-6">
          <SectionTitle icon={Clock} title="Publisher Experience" number="2" />
          <SelectField
            label="How long have you been generating calls?"
            required
            options={EXPERIENCE_OPTIONS}
            value={formData.experience_duration}
            onChange={(e) => handleChange("experience_duration", e.target.value)}
          />
          <div className="mt-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Previous Companies / Networks you worked with
            </label>
            <textarea
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-slate-800 min-h-[80px]"
              value={formData.previous_companies}
              onChange={(e) => handleChange("previous_companies", e.target.value)}
              placeholder="Enter company/network names (e.g., Ringba, Retreaver, other PPC networks)&#10;One per line or separated by commas"
            />
            <p className="text-slate-400 text-xs mt-1">List the pay-per-call networks or companies you&apos;ve previously worked with</p>
          </div>
          <div className="mt-4">
            <SelectField
              label="How did you hear about us?"
              required
              options={HOW_HEARD_OPTIONS}
              value={formData.how_heard_about_us}
              onChange={(e) => handleChange("how_heard_about_us", e.target.value)}
            />
          </div>
        </div>

        {/* Section 3: Call Tracking */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 mb-6">
          <SectionTitle icon={Headphones} title="Call Tracking Platform" number="3" />
          <SelectField
            label="Which call tracking platform do you use?"
            required
            options={CALL_TRACKING_PLATFORMS}
            value={formData.call_tracking_platform}
            onChange={(e) => handleChange("call_tracking_platform", e.target.value)}
          />
        </div>

        {/* Section 5: Call Type */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 mb-6">
          <SectionTitle icon={Phone} title="Call Type" number="4" />
          <SelectField
            label="What type of calls do you generate?"
            required
            options={CALL_TYPES}
            value={formData.call_type}
            onChange={(e) => handleChange("call_type", e.target.value)}
          />
        </div>

        {/* Section 6: Daily Volume */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 mb-6">
          <SectionTitle icon={BarChart3} title="Estimated Daily Call Volume" number="5" />
          <SelectField
            label="How many calls can you generate daily?"
            required
            options={DAILY_VOLUMES}
            value={formData.daily_call_volume}
            onChange={(e) => handleChange("daily_call_volume", e.target.value)}
          />
        </div>

        {/* Section 6: How do we pay you? (Payment Method) */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 mb-6" data-testid="payment-method-section">
          <SectionTitle icon={CreditCard} title="How Do We Pay You?" number="6" />
          <p className="text-slate-500 text-xs sm:text-sm mb-4">
            Choose your preferred payment method and enter the receiving details. <span className="text-red-500 font-semibold">Required.</span>
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Payment Method <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-slate-800 bg-white"
                value={formData.payment_method}
                onChange={(e) => handleChange("payment_method", e.target.value)}
                data-testid="payment-method-select"
              >
                <option value="">Select a method…</option>
                <option value="Payoneer">Payoneer</option>
                <option value="Wise">Wise</option>
                <option value="ACH Bank Transfer">ACH Bank Transfer</option>
                <option value="Other">Other</option>
              </select>
              <p className="text-slate-400 text-xs mt-1">All payments issued from our USA checking account.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Payment Details <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-slate-800"
                value={formData.payment_details}
                onChange={(e) => handleChange("payment_details", e.target.value)}
                placeholder={
                  formData.payment_method === "Payoneer" ? "Payoneer email or ID"
                  : formData.payment_method === "Wise" ? "Wise email / handle"
                  : formData.payment_method === "ACH Bank Transfer" ? "Bank name, account #, routing #"
                  : formData.payment_method === "Other" ? "Describe the method + account details"
                  : "Email, account, or routing details for the chosen method"
                }
                data-testid="payment-details-input"
              />
              <p className="text-slate-400 text-xs mt-1">e.g. Payoneer email, Wise handle, or bank details.</p>
            </div>
          </div>
        </div>

        {/* Section 7: Verticals */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 mb-6">
          <SectionTitle icon={Target} title="Offers / Verticals" number="7" />
          <MultiSelect
            label="Select verticals you want to promote"
            required
            options={VERTICALS}
            selected={formData.verticals}
            onChange={(val) => handleChange("verticals", val)}
          />
        </div>

        {/* Section 10: Compliance Requirements */}
        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm mb-6" data-testid="compliance-section">
          {/* Accent bar */}
          <div className="h-1 bg-gradient-to-r from-red-500 via-rose-500 to-amber-500" />

          <div className="p-5 sm:p-8">
            {/* Header — stacked on mobile so the badge doesn't crush the title */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-2">
              <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center flex-shrink-0">
                  <ShieldAlert className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg sm:text-xl font-semibold text-slate-900 font-['Poppins'] leading-tight">
                    Compliance Requirements
                  </h3>
                  <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                    Tick each rule below to confirm you have read and agree to follow it.
                  </p>
                </div>
              </div>

              {/* Progress badge */}
              <div
                className={`self-start inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold border whitespace-nowrap flex-shrink-0 ${
                  allCompliance
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-slate-50 text-slate-600 border-slate-200"
                }`}
                data-testid="compliance-progress"
              >
                {allCompliance ? (
                  <CheckCircle className="w-3.5 h-3.5" />
                ) : (
                  <AlertCircle className="w-3.5 h-3.5" />
                )}
                {ackedCount} of {COMPLIANCE_RULES.length} acknowledged
              </div>
            </div>

            {/* Zero Tolerance group */}
            <div className="mt-5 mb-5">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-red-50 text-red-700 text-[11px] font-semibold border border-red-200 uppercase tracking-wider">
                  <ShieldX className="w-3 h-3" />
                  Zero Tolerance
                </span>
                <span className="text-xs text-slate-400">Immediate termination — no payout</span>
              </div>

              <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden">
                {COMPLIANCE_RULES.filter((r) => r.severity === "red").map((rule) => {
                  const checked = complianceAcks[rule.key];
                  return (
                    <li key={rule.key}>
                      <button
                        type="button"
                        onClick={() => toggleAck(rule.key)}
                        data-testid={`compliance-ack-${rule.key}`}
                        className={`w-full text-left flex items-start gap-3 p-3.5 transition-colors ${
                          checked ? "bg-emerald-50/60" : "bg-white hover:bg-slate-50/60"
                        }`}
                      >
                        <span
                          className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                            checked
                              ? "bg-emerald-500 border-emerald-500"
                              : "bg-white border-slate-300"
                          }`}
                          aria-checked={checked}
                          role="checkbox"
                        >
                          {checked && <CheckCircle className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-semibold text-slate-900">
                            {rule.title}
                          </span>
                          <span className="block text-xs text-slate-500 mt-0.5">{rule.desc}</span>
                        </span>
                        <XCircle className="w-4 h-4 text-red-500 mt-1 flex-shrink-0" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* First Warning group */}
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-50 text-amber-700 text-[11px] font-semibold border border-amber-200 uppercase tracking-wider">
                  <AlertCircle className="w-3 h-3" />
                  First Warning
                </span>
                <span className="text-xs text-slate-400">Repeated violations lead to termination</span>
              </div>

              <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden">
                {COMPLIANCE_RULES.filter((r) => r.severity === "amber").map((rule) => {
                  const checked = complianceAcks[rule.key];
                  return (
                    <li key={rule.key}>
                      <button
                        type="button"
                        onClick={() => toggleAck(rule.key)}
                        data-testid={`compliance-ack-${rule.key}`}
                        className={`w-full text-left flex items-start gap-3 p-3.5 transition-colors ${
                          checked ? "bg-emerald-50/60" : "bg-white hover:bg-slate-50/60"
                        }`}
                      >
                        <span
                          className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                            checked
                              ? "bg-emerald-500 border-emerald-500"
                              : "bg-white border-slate-300"
                          }`}
                          aria-checked={checked}
                          role="checkbox"
                        >
                          {checked && <CheckCircle className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-semibold text-slate-900">
                            {rule.title}
                          </span>
                          <span className="block text-xs text-slate-500 mt-0.5">{rule.desc}</span>
                        </span>
                        <AlertTriangle className="w-4 h-4 text-amber-500 mt-1 flex-shrink-0" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Violation Policy footer */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 sm:p-5 flex gap-3">
              <Info className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-slate-700">
                <p className="font-semibold text-slate-900 mb-1">Violation Policy</p>
                <p className="leading-relaxed">
                  A first warning may be issued for minor violations. However, if the violation is repeated, the{' '}
                  <span className="font-semibold text-red-700">
                    publisher account will be terminated immediately with no payout
                  </span>
                  .
                </p>
              </div>
            </div>

            {/* All-acknowledged success banner */}
            {allCompliance && (
              <div
                className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3"
                data-testid="compliance-all-acked"
              >
                <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold text-emerald-900">All compliance rules acknowledged.</p>
                  <p className="text-emerald-700 mt-0.5">Thank you — you may now complete and submit your application.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Dynamic Traffic Details + Proof — driven by the picker at the top */}
        <TrafficDetailsAndProof
          primaryTrafficSource={primaryTrafficSource}
          sourceDetails={sourceDetails}
          setSourceDetails={setSourceDetails}
          proofFiles={proofFiles}
          proofPreviews={proofPreviews}
          handleProofSelect={handleProofSelect}
          removeProof={removeProof}
          verticals={formData.verticals}
          slotProofFiles={slotProofFiles}
          slotProofPreviews={slotProofPreviews}
          handleSlotProofSelect={handleSlotProofSelect}
          removeSlotProof={removeSlotProof}
        />

        {/* Path-specific terms & mandatory acknowledgement */}
        <DynamicTrafficTerms
          primaryTrafficSource={primaryTrafficSource}
          agreed={agreedToTrafficTerms}
          setAgreed={setAgreedToTrafficTerms}
        />

        {/* Section 10: Digital Signature */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 mb-6">
          <SectionTitle icon={FileCheck} title="Digital Signature" number="8" />
          
          <p className="text-slate-600 text-sm mb-4">
            Please sign below to confirm your agreement to the Digital Master Media LLC Publisher Terms and Compliance Policy. 
            A copy of this agreement will be sent to your email.
          </p>
          
          <div className="mb-5">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Type your full legal name as your digital signature *
            </label>
            <input
              type="text"
              value={formData.digital_signature || ""}
              onChange={(e) => handleChange("digital_signature", e.target.value)}
              required
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-['Brush_Script_MT',cursive] text-2xl text-blue-800"
              placeholder="Your Full Name"
              style={{ fontFamily: "'Brush Script MT', cursive" }}
              data-testid="digital-signature-input"
            />
          </div>

          <SignaturePad
            value={formData.signature_image}
            onChange={(dataUrl) => handleChange("signature_image", dataUrl)}
          />
        </div>

        {/* Section 14: Agreement */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 mb-6">
          <SectionTitle icon={FileCheck} title="Agreement" number="9" />
          
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.agreed_to_terms}
              onChange={(e) => handleChange("agreed_to_terms", e.target.checked)}
              className="w-5 h-5 mt-0.5 rounded border-slate-300 text-green-500 focus:ring-green-500"
            />
            <span className="text-slate-700 text-sm">
              I agree to the <strong>Digital Master Media LLC Publisher Terms and Compliance Policy</strong>. 
              I understand that a PDF copy of this application will be sent to my email as a publisher agreement.
            </span>
          </label>
        </div>

        {/* Auto-retry info banner — shown briefly during silent retries on transient network errors */}
        {submitStatus?.type === "info" && (
          <div
            className="bg-blue-50 border border-blue-300 rounded-lg p-4 mb-6 flex items-start gap-3"
            data-testid="submit-info-banner"
            role="status"
          >
            <Loader2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5 animate-spin" />
            <p className="text-blue-700 text-sm whitespace-pre-line leading-relaxed">
              {submitStatus.message}
            </p>
          </div>
        )}

        {/* Error Message — preserves line-breaks so multi-field validation
            checklists render as a real list, not a wall of text. */}
        {submitStatus?.type === "error" && (
          <div
            className="bg-red-50 border border-red-300 rounded-lg p-4 mb-6 flex items-start gap-3"
            data-testid="submit-error-banner"
            role="alert"
          >
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-700 text-sm whitespace-pre-line leading-relaxed">
                {submitStatus.message}
              </p>
              {/* Inline Try Again button for network-level errors — saves the user from
                  scrolling all the way back down to the submit button on mobile. */}
              {/failed to fetch|networkerror|load failed|connection|couldn't reach|browser extension|took too long/i.test(submitStatus.message || "") && (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="submit-error-retry-btn"
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-60"
                >
                  {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Try submitting again
                </button>
              )}
            </div>
          </div>
        )}

        {/* Nudge if compliance not fully acknowledged */}
        {!allCompliance && (
          <div
            className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3"
            data-testid="compliance-incomplete-nudge"
          >
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-amber-900">
                Please acknowledge all {COMPLIANCE_RULES.length} compliance rules
              </p>
              <p className="text-amber-800 mt-0.5">
                Scroll up to the <strong>Compliance Requirements</strong> section and tick each rule to continue.
                ({ackedCount} of {COMPLIANCE_RULES.length} acknowledged)
              </p>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting || emailAvailability.status === "taken" || !formData.agreed_to_terms || !formData.digital_signature || !formData.signature_image || !allCompliance || !formData.messaging_platform || !(formData.messaging_handle || "").trim()}
          data-testid="publisher-submit-btn"
          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-slate-400 disabled:to-slate-400 text-white font-bold text-lg py-4 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              Sign & Submit Application
            </>
          )}
        </button>

        <p className="text-center text-slate-500 text-xs mt-4">
          By submitting this form, you confirm that all information provided is accurate. 
          A PDF agreement will be emailed to you and our team.
        </p>
        </>
        )}
      </form>

      {/* Footer */}
      <Footer />
    </div>
  );
};

/* ─── Inline: Traffic Source Picker (gateway at /apply) ───────────── */
const TrafficSourcePicker = ({ primaryTrafficSource, onPick }) => {
  return (
  <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-6 mb-6" data-testid="traffic-source-section">
    <SectionTitle icon={MousePointer} title="How will you generate calls?" />
    <p className="text-slate-500 text-sm mb-5">
      Choose your main traffic source below to start the application. <span className="text-red-500 font-semibold">Required.</span>
    </p>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 sm:gap-3">
      {PRIMARY_SOURCES.map((s) => {
        const Icon = s.icon;
        const active = primaryTrafficSource === s.key;
        return (
          <button
            type="button"
            key={s.key}
            onClick={() => onPick(s.key)}
            className={`group relative overflow-hidden rounded-2xl border p-3 sm:p-4 text-left transition-all min-h-[110px] sm:min-h-[132px] flex flex-col active:scale-[0.98] ${
              active
                ? "border-blue-500 bg-blue-50/60 shadow-md ring-2 ring-blue-500/40"
                : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
            }`}
            data-testid={`traffic-source-${s.key}`}
          >
            <div className={`w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center shadow-sm mb-2 sm:mb-3`}>
              <Icon className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-semibold text-slate-800 text-[13px] sm:text-sm leading-tight">{s.label}</div>
            <div className="text-slate-500 text-[11px] mt-0.5 leading-snug">{s.tagline}</div>
            {active && (
              <div className="absolute top-2 right-2 sm:top-3 sm:right-3 w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                <CheckCircle className="w-3.5 h-3.5 text-white" strokeWidth={3} />
              </div>
            )}
          </button>
        );
      })}
    </div>
  </div>
  );
};

/* ─── Dynamic Traffic Terms + mandatory checkbox ─────────────────── */
const DynamicTrafficTerms = ({ primaryTrafficSource, agreed, setAgreed }) => {
  const chosen = sourceByKey(primaryTrafficSource);
  const terms = TERMS_BY_SOURCE[primaryTrafficSource];
  if (!chosen || !terms) return null;
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-6 mb-6" data-testid="dynamic-terms-section">
      <SectionTitle icon={CreditCard} title={`${chosen.label} — Payment & Terms`} />
      <div className="rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50 to-slate-50 p-5 mb-4" data-testid="dynamic-terms-text">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <TermTile label="Threshold" value={terms.threshold} />
          <TermTile label="Buffer" value={terms.buffer} />
          <TermTile label="Payment" value={terms.payment} />
        </div>
      </div>
      <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="dynamic-terms-agree-label">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-1 w-4 h-4 accent-blue-600 flex-shrink-0"
          data-testid="dynamic-terms-agree-checkbox"
        />
        <span className="text-sm text-slate-700">
          <span className="font-semibold">I agree to these terms</span> — Threshold <span className="font-mono">{terms.threshold}</span> · Buffer <span className="font-mono">{terms.buffer}</span> · Payment <span className="font-mono">{terms.payment}</span>. <span className="text-red-500 font-semibold">Required.</span>
        </span>
      </label>
    </div>
  );
};

const TermTile = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-blue-500">{label}</div>
    <div className="text-slate-900 font-semibold text-sm mt-1 font-mono">{value}</div>
  </div>
);

/* ─── Reusable: dynamic list of N inputs (add/remove up to `max`) ── */
const ListFieldInput = ({ name, values, onChange, max = 3, placeholder = "" }) => {
  const list = values.length ? values : [""];
  const setAt = (i, val) => {
    const next = [...list];
    next[i] = val;
    onChange(next);
  };
  const addRow = () => {
    if (list.length >= max) return;
    onChange([...list, ""]);
  };
  const removeAt = (i) => {
    const next = list.filter((_, idx) => idx !== i);
    onChange(next.length ? next : [""]);
  };
  return (
    <div className="space-y-2" data-testid={`source-list-${name}`}>
      {list.map((v, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            type="text"
            className="flex-1 px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-slate-800"
            value={v}
            onChange={(e) => setAt(i, e.target.value)}
            placeholder={placeholder || `${name} ${i + 1}`}
            data-testid={`source-list-${name}-${i}`}
          />
          {list.length > 1 && (
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50"
              aria-label="Remove"
              data-testid={`source-list-${name}-remove-${i}`}
            >
              <XIcon className="w-4 h-4" strokeWidth={2.5} />
            </button>
          )}
        </div>
      ))}
      {list.length < max && (
        <button
          type="button"
          onClick={addRow}
          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          data-testid={`source-list-${name}-add`}
        >
          + Add another ({list.length}/{max})
        </button>
      )}
    </div>
  );
};

/* ─── Inline: Facebook Marketplace pricing notice + mandatory disclaimer ─── */
const FB_MARKETPLACE_DISCLAIMER =
  "This is a free listing aimed at helping homeowners connect with local contractors. All contractors listed here are independent, and this platform does not guarantee any work performed. It is the responsibility of homeowners to verify that the contractor they hire has the proper licenses and insurance for the work being done.";

const FbMarketplaceRules = () => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(FB_MARKETPLACE_DISCLAIMER).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="mt-5 space-y-4">
      <div className="flex gap-3 bg-amber-50 border-l-4 border-amber-400 text-amber-900 rounded-xl p-4 text-sm" data-testid="fb-marketplace-price-notice">
        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <p>
          <strong>All Facebook Marketplace postings must be priced at $12,345.</strong> We do not allow posts with lower prices (e.g. $10, $20, $30, $40, etc.).
        </p>
      </div>
      <div>
        <div className="flex items-center justify-between gap-3 mb-1">
          <label className="block text-sm font-medium text-slate-700">
            Required Disclaimer <span className="text-red-500">*</span>
          </label>
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-blue-600 hover:bg-blue-50 hover:border-blue-300 transition-colors"
            data-testid="fb-marketplace-disclaimer-copy"
          >
            {copied ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> : <FileText className="w-3.5 h-3.5" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-2">You must include this disclaimer in every Facebook Marketplace post.</p>
        <div
          className="w-full px-4 py-3 border border-slate-200 bg-slate-50 rounded-xl text-sm text-slate-700 leading-relaxed select-all"
          data-testid="fb-marketplace-disclaimer-text"
        >
          {FB_MARKETPLACE_DISCLAIMER}
        </div>
      </div>
    </div>
  );
};

/* ─── Inline: Dynamic Traffic Details + Proof (bottom of form) ─────── */
const TrafficDetailsAndProof = ({
  primaryTrafficSource, sourceDetails, setSourceDetails,
  proofFiles, proofPreviews, handleProofSelect, removeProof,
  verticals = [], slotProofFiles = {}, slotProofPreviews = {}, handleSlotProofSelect, removeSlotProof,
}) => {
  const chosen = sourceByKey(primaryTrafficSource);
  const setField = (name, value) => setSourceDetails((prev) => ({ ...prev, [name]: value }));

  if (!chosen) {
    return (
      <div className="bg-amber-50 border-l-4 border-amber-400 text-amber-900 rounded-xl p-4 mb-6 text-sm" data-testid="traffic-details-empty">
        Pick a <strong>Primary Traffic Source</strong> at the top of the form to reveal the required traffic-details section.
      </div>
    );
  }

  return (
    <>
      {/* Traffic Details (all mandatory) */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-6 mb-6" data-testid="source-details-panel">
        <SectionTitle icon={FileText} title={`${chosen.label} — Traffic Details`} />
        <p className="text-slate-500 text-xs mb-4">All fields below are <span className="text-red-500 font-semibold">mandatory</span> for {chosen.label} traffic.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {chosen.fields.map((f) => (
            <div key={f.name} className={f.textarea || f.listField ? "sm:col-span-2" : ""}>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {f.label} <span className="text-red-500">*</span>
              </label>
              {f.multiSelect ? (
                <MultiStateSelect
                  options={f.options || []}
                  values={Array.isArray(sourceDetails[f.name]) ? sourceDetails[f.name] : []}
                  onChange={(arr) => setField(f.name, arr)}
                  testid={`source-field-${f.name}`}
                />
              ) : f.type === "file" ? (
                <div>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,application/pdf"
                    className="hidden"
                    id={`source-file-${f.name}`}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) setField(f.name, file.name);
                      handleProofSelect(e);
                    }}
                    data-testid={`source-field-${f.name}`}
                  />
                  <label
                    htmlFor={`source-file-${f.name}`}
                    className="inline-flex items-center gap-2 px-4 py-3 border border-dashed border-slate-300 rounded-xl text-sm font-medium text-slate-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/50 cursor-pointer transition-all"
                  >
                    <Camera className="w-4 h-4" />
                    {sourceDetails[f.name]
                      ? `Attached: ${sourceDetails[f.name]}`
                      : "Choose screenshot…"}
                  </label>
                  <p className="text-[11px] text-slate-400 mt-1.5">
                    The file is added to your Proof &amp; Screenshots section below (JPG / PNG / PDF · 10 MB max).
                  </p>
                </div>
              ) : f.listField ? (
                <ListFieldInput
                  name={f.name}
                  max={f.max || 3}
                  placeholder={f.itemPlaceholder}
                  values={Array.isArray(sourceDetails[f.name]) ? sourceDetails[f.name] : [""]}
                  onChange={(arr) => setField(f.name, arr)}
                />
              ) : f.textarea ? (
                <textarea
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-slate-800 min-h-[80px]"
                  value={sourceDetails[f.name] || ""}
                  onChange={(e) => setField(f.name, e.target.value)}
                  placeholder={f.placeholder}
                  data-testid={`source-field-${f.name}`}
                />
              ) : (
                <input
                  type={f.type || "text"}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-slate-800"
                  value={sourceDetails[f.name] || ""}
                  onChange={(e) => setField(f.name, e.target.value)}
                  placeholder={f.placeholder}
                  data-testid={`source-field-${f.name}`}
                />
              )}
            </div>
          ))}
        </div>
        {chosen.key === "fb_marketplace" && (
          <FbMarketplaceRules />
        )}
      </div>

      {/* Proof upload — structured 3-slot sets per selected offer for Google/Meta; generic pool otherwise */}
      {chosen.proofSlots ? (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-6 mb-6" data-testid="proof-upload-panel">
          <SectionTitle icon={Camera} title={`${chosen.label} Proof & Screenshots`} />
          <p className="text-slate-500 text-sm mb-4">
            Upload <span className="text-red-500 font-semibold">3 mandatory screenshots for each offer</span> you selected in the Offers / Verticals section. <span className="text-slate-400">(JPG / PNG / PDF · 10 MB each)</span>
          </p>

          {(!Array.isArray(verticals) || verticals.length === 0) ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="proof-no-verticals">
              Select at least one offer in the <strong>Offers / Verticals</strong> section above — a separate 3-screenshot proof set will appear here for each offer you apply for.
            </div>
          ) : (
            <div className="space-y-5">
              {verticals.map((v) => (
                <div key={v} className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid={`proof-set-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <h4 className="font-semibold text-slate-900 text-sm mb-3">{chosen.label} Proof — {v}</h4>
                  <div className="grid sm:grid-cols-3 gap-4">
                    {chosen.proofSlots.map((slot) => {
                      const file = slotProofFiles?.[v]?.[slot.key];
                      const preview = slotProofPreviews?.[v]?.[slot.key];
                      const inputId = `slot-proof-${v}-${slot.key}`.replace(/[^a-zA-Z0-9-_]/g, "-");
                      return (
                        <div key={slot.key} className="rounded-xl border border-slate-200 bg-white p-3">
                          <p className="text-xs font-semibold text-slate-800 leading-snug">{slot.label} <span className="text-red-500">*</span></p>
                          <p className="text-[11px] text-slate-500 mt-1 mb-2.5 leading-snug">{slot.hint}</p>
                          <input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,application/pdf"
                            className="hidden"
                            id={inputId}
                            onChange={handleSlotProofSelect(v, slot.key)}
                            data-testid={`input-${inputId}`}
                          />
                          {preview ? (
                            <div className="relative group rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
                              {preview.pdf ? (
                                <div className="h-20 flex flex-col items-center justify-center p-2 text-slate-600">
                                  <FileText className="w-6 h-6 mb-1" />
                                  <div className="text-[10px] text-center truncate w-full">{preview.name}</div>
                                </div>
                              ) : (
                                <img src={preview.url} alt={preview.name} className="w-full h-20 object-cover" />
                              )}
                              <button
                                type="button"
                                onClick={() => removeSlotProof(v, slot.key)}
                                className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-md"
                                data-testid={`remove-${inputId}`}
                                aria-label="Remove"
                              >
                                <XIcon className="w-3 h-3" strokeWidth={3} />
                              </button>
                            </div>
                          ) : (
                            <label
                              htmlFor={inputId}
                              className="flex items-center justify-center gap-2 w-full h-20 rounded-lg border-2 border-dashed border-slate-300 hover:border-blue-500 hover:bg-blue-50/40 text-slate-500 hover:text-blue-600 text-xs font-medium cursor-pointer transition-all"
                              data-testid={`label-${inputId}`}
                            >
                              <Upload className="w-4 h-4" />
                              Upload screenshot
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No-random-images rule */}
          <div className="mt-5 flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 p-3.5" data-testid="proof-random-images-warning">
            <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" strokeWidth={2.25} />
            <p className="text-xs text-red-700 leading-relaxed">
              <span className="font-bold">Warning:</span> Only upload relevant <span className="font-semibold">{chosen.label}</span> screenshots.
              Random images or stock photos will result in an <span className="font-bold">immediate ban</span> from our network.
            </p>
          </div>
        </div>
      ) : (
      /* Proof upload (mandatory — at least 1 file) */
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-6 mb-6" data-testid="proof-upload-panel">
        <SectionTitle icon={Camera} title={`${chosen.label} Proof & Screenshots`} />
        <p className="text-slate-500 text-sm mb-3">
          {chosen.proofHint} {!chosen.proofHint.includes("Required") && <span className="text-red-500 font-semibold">Required.</span>} <span className="text-slate-400">(up to 3 files · JPG / PNG / PDF · 10 MB each)</span>
        </p>

        {proofFiles.length < 3 && (
          <label className="flex items-center justify-center gap-2 w-full h-24 rounded-xl border-2 border-dashed border-slate-300 hover:border-blue-500 hover:bg-blue-50/40 text-slate-500 hover:text-blue-600 font-medium text-sm cursor-pointer transition-all" data-testid="proof-upload-label">
            <Upload className="w-4 h-4" />
            Click to upload — {3 - proofFiles.length} more allowed
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,application/pdf"
              onChange={handleProofSelect}
              multiple
              className="hidden"
              data-testid="proof-upload-input"
            />
          </label>
        )}

        {proofPreviews.length > 0 && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="proof-previews">
            {proofPreviews.map((p, i) => (
              <div key={i} className="relative group rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                {p.pdf ? (
                  <div className="aspect-square flex flex-col items-center justify-center p-4 text-slate-600">
                    <FileText className="w-10 h-10 mb-2" />
                    <div className="text-xs text-center truncate w-full">{p.name}</div>
                  </div>
                ) : (
                  <img src={p.url} alt={p.name} className="w-full aspect-square object-cover" />
                )}
                <button
                  type="button"
                  onClick={() => removeProof(i)}
                  className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-md sm:opacity-70 sm:group-hover:opacity-100 transition-opacity"
                  data-testid={`proof-remove-${i}`}
                  aria-label="Remove"
                >
                  <XIcon className="w-3.5 h-3.5" strokeWidth={3} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* No-random-images rule */}
        <div className="mt-5 flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 p-3.5" data-testid="proof-random-images-warning">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" strokeWidth={2.25} />
          <p className="text-xs text-red-700 leading-relaxed">
            <span className="font-bold">Warning:</span> Only upload relevant <span className="font-semibold">{chosen.label}</span> screenshots.
            Random images or stock photos will result in an <span className="font-bold">immediate ban</span> from our network.
          </p>
        </div>
      </div>
      )}
    </>
  );
};

/* ─── Signature Pad ─────────────────────────────────────────────────
   Canvas-based handwritten signature capture. The parent owns the state
   as a base64-encoded PNG dataURL (`value`) so it round-trips cleanly
   through form validation and FormData assembly. Supports mouse + touch
   pointer input, high-DPI rendering, and a Clear button.
─────────────────────────────────────────────────────────────────── */
const SignaturePad = ({ value, onChange, disabled = false }) => {
  const canvasRef = React.useRef(null);
  const drawingRef = React.useRef(false);
  const lastPtRef = React.useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = React.useState(Boolean(value));

  // Prepare the canvas at mount + on resize. We render at devicePixelRatio
  // for crisp strokes on Retina / high-DPI displays and re-apply the ink
  // colour + line style each time we reset the bitmap.
  const configureCanvas = React.useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const cssW = canvas.clientWidth;
    const cssH = canvas.clientHeight;
    canvas.width = Math.max(1, Math.floor(cssW * ratio));
    canvas.height = Math.max(1, Math.floor(cssH * ratio));
    const ctx = canvas.getContext("2d");
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.lineWidth = 2.4;
    ctx.strokeStyle = "#0f172a";
    // Restore existing ink if the state already has an image (e.g. after
    // resize) so users don't lose their drawing when the layout reflows.
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, cssW, cssH);
      img.src = value;
    }
  }, [value]);

  React.useEffect(() => {
    configureCanvas();
    const onResize = () => configureCanvas();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const src = e.touches && e.touches[0] ? e.touches[0] : e;
    return {
      x: src.clientX - rect.left,
      y: src.clientY - rect.top,
    };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawingRef.current = true;
    lastPtRef.current = getPoint(e);
    setHasInk(true);
  };

  const move = (e) => {
    if (!drawingRef.current || disabled) return;
    e.preventDefault();
    const p = getPoint(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastPtRef.current.x, lastPtRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPtRef.current = p;
  };

  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onChange(dataUrl);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange("");
  };

  return (
    <div data-testid="signature-pad">
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-slate-700">
          Draw your handwritten signature <span className="text-red-500">*</span>
        </label>
        {(hasInk || value) && (
          <button
            type="button"
            onClick={clear}
            className="text-xs font-semibold text-slate-500 hover:text-red-600 underline underline-offset-2"
            data-testid="signature-clear-btn"
          >
            Clear
          </button>
        )}
      </div>
      <div
        className={`relative rounded-xl border-2 border-dashed transition-colors ${
          hasInk ? "border-blue-400 bg-white" : "border-slate-300 bg-slate-50"
        }`}
      >
        <canvas
          ref={canvasRef}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          className="w-full h-40 sm:h-48 touch-none cursor-crosshair rounded-xl"
          data-testid="signature-canvas"
          aria-label="Signature drawing area"
        />
        {!hasInk && !value && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-slate-400 text-sm select-none">
            Sign here with your mouse or finger
          </div>
        )}
      </div>
      <p className="mt-1 text-[11px] text-slate-500">
        Your signature will be embedded in the final Master Agreement PDF, along
        with your IP address and timestamp — no printing or scanning required.
      </p>
    </div>
  );
};

export default PublisherApplicationPage;
